import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { htmlSafe } from '@ember/template';
import {
  setupCanvas,
  drawGridLines,
  drawYAxisLabels,
  drawXAxisLabels,
} from '../../utils/charts/canvas-utils.ts';
import { linearScale, niceAxisTicks } from '../../utils/charts/chart-scales.ts';
import { computeLayout, formatAxisValue, formatNumber } from '../../utils/charts/chart-layout.ts';
import { DEFAULT_CHART_COLORS, getThemeColors } from '../../utils/charts/color-palette.ts';
import type { ChartDataPoint, ChartOptions, AxisOptions } from '../../types/charts.ts';

// ---------------------------------------------------------------------------
// LineChart – Canvas-rendered line chart with optional point markers
// ---------------------------------------------------------------------------

interface LineChartSignature {
  Args: ChartOptions &
    AxisOptions & {
      /** Chart data points */
      data: ChartDataPoint[];
      /** Fixed height in pixels (default: 300) */
      height?: number;
      /** Show point markers (default: true) */
      showPoints?: boolean;
      /** Line stroke width (default: 2) */
      strokeWidth?: number;
    };
  Element: HTMLDivElement;
}

const DEFAULT_HEIGHT = 300;

export default class LineChart extends Component<LineChartSignature> {
  @tracked containerWidth = 400;
  @tracked hoveredIndex: number | null = null;
  @tracked tooltipX = 0;
  @tracked tooltipY = 0;

  get height() {
    return this.args.height ?? DEFAULT_HEIGHT;
  }

  get color() {
    return this.args.colors?.[0] ?? DEFAULT_CHART_COLORS[1]!;
  }

  get data() {
    return this.args.data ?? [];
  }

  get showPoints() {
    return this.args.showPoints !== false;
  }

  get canvasStyle() {
    return htmlSafe(`width: ${this.containerWidth}px; height: ${this.height}px;`);
  }

  get tooltipData() {
    if (this.hoveredIndex === null || !this.data[this.hoveredIndex]) return null;
    const d = this.data[this.hoveredIndex]!;
    return { category: d.category, value: formatNumber(d.value) };
  }

  get tooltipStyle() {
    if (!this.tooltipData) return htmlSafe('display: none;');
    return htmlSafe(`left: ${this.tooltipX}px; top: ${this.tooltipY}px;`);
  }

  private pointPositions: Array<{ x: number; y: number; index: number }> = [];

  installCanvas = modifier((element: HTMLElement) => {
    const canvas = element.querySelector('canvas');
    if (!canvas) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        this.containerWidth = entry.contentRect.width;
        this.draw(canvas);
      }
    });
    observer.observe(element);
    this.containerWidth = element.clientWidth || 400;
    this.draw(canvas);

    const onMove = (e: MouseEvent) => this.handleMouseMove(e, canvas);
    const onLeave = () => {
      this.hoveredIndex = null;
      this.draw(canvas);
    };
    const onClick = (e: MouseEvent) => this.handleClick(e, canvas);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onClick);

    return () => {
      observer.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onClick);
    };
  });

  draw(canvas: HTMLCanvasElement) {
    const width = this.containerWidth;
    const height = this.height;
    const data = this.data;
    if (!data.length) return;

    const shouldRotate = data.length > 5;
    const bottomMargin = shouldRotate ? 80 : 40;
    const layout = computeLayout(width, height, { bottom: bottomMargin, left: 55 });
    const theme = getThemeColors();

    const ctx = setupCanvas(canvas, width, height);
    ctx.clearRect(0, 0, width, height);

    // Scales
    const maxVal = Math.max(...data.map((d) => d.value), 0);
    const ticks = niceAxisTicks(0, maxVal);
    const yScale = linearScale(
      [0, ticks[ticks.length - 1]!],
      [layout.top + layout.innerHeight, layout.top]
    );

    // X positions — evenly spaced
    const xStep = data.length > 1 ? layout.innerWidth / (data.length - 1) : 0;
    const xPositions = data.map((_, i) => layout.left + i * xStep);

    // Grid
    drawGridLines(ctx, ticks, yScale, layout.left, layout.left + layout.innerWidth, theme.grid);

    // Y axis
    const formatFn = this.args.formatValue ?? formatAxisValue;
    drawYAxisLabels(ctx, ticks, yScale, layout.left - 8, theme.textMuted, formatFn);

    // X axis
    const labels = data.map((d) => d.category);
    drawXAxisLabels(
      ctx,
      labels,
      xPositions,
      layout.top + layout.innerHeight + 8,
      theme.textMuted,
      shouldRotate
    );

    // Store point positions for hit testing
    this.pointPositions = data.map((d, i) => ({
      x: xPositions[i]!,
      y: yScale(d.value),
      index: i,
    }));

    // Draw line
    const strokeWidth = this.args.strokeWidth ?? 2;
    ctx.strokeStyle = this.color;
    ctx.lineWidth = strokeWidth;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    this.pointPositions.forEach((p, i) => {
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.stroke();

    // Draw points
    if (this.showPoints) {
      this.pointPositions.forEach((p, i) => {
        const isHovered = this.hoveredIndex === i;
        const radius = isHovered ? 6 : 4;

        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();

        // White inner dot
        ctx.fillStyle = theme.tooltipBg;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius - 2, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    // Crosshair for hovered point
    if (this.hoveredIndex !== null && this.pointPositions[this.hoveredIndex]) {
      const p = this.pointPositions[this.hoveredIndex]!;
      ctx.strokeStyle = theme.grid;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);

      // Vertical
      ctx.beginPath();
      ctx.moveTo(p.x, layout.top);
      ctx.lineTo(p.x, layout.top + layout.innerHeight);
      ctx.stroke();

      // Horizontal
      ctx.beginPath();
      ctx.moveTo(layout.left, p.y);
      ctx.lineTo(layout.left + layout.innerWidth, p.y);
      ctx.stroke();

      ctx.setLineDash([]);
    }

    // Axis labels
    if (this.args.xLabel) {
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(this.args.xLabel, layout.left + layout.innerWidth / 2, height - 4);
    }
    if (this.args.yLabel) {
      ctx.save();
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.translate(14, layout.top + layout.innerHeight / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillText(this.args.yLabel, 0, 0);
      ctx.restore();
    }
  }

  handleMouseMove(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    // Find nearest point within 20px
    let closest: { dist: number; index: number } | null = null;
    for (const p of this.pointPositions) {
      const dist = Math.hypot(mx - p.x, my - p.y);
      if (dist < 20 && (!closest || dist < closest.dist)) {
        closest = { dist, index: p.index };
      }
    }

    if (closest) {
      this.hoveredIndex = closest.index;
      const p = this.pointPositions[closest.index]!;
      this.tooltipX = rect.left + p.x;
      this.tooltipY = rect.top + p.y - 8;
      this.draw(canvas);
    } else if (this.hoveredIndex !== null) {
      this.hoveredIndex = null;
      this.draw(canvas);
    }
  }

  handleClick(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    let closest: { dist: number; index: number } | null = null;
    for (const p of this.pointPositions) {
      const dist = Math.hypot(mx - p.x, my - p.y);
      if (dist < 20 && (!closest || dist < closest.dist)) {
        closest = { dist, index: p.index };
      }
    }

    if (closest && this.args.onClick && this.data[closest.index]) {
      this.args.onClick(this.data[closest.index]!);
    }
  }

  <template>
    <div
      data-test-chart
      data-test-chart-type="line"
      class="relative w-full"
      {{this.installCanvas}}
      ...attributes
    >
      <canvas style={{this.canvasStyle}}></canvas>

      {{#if this.tooltipData}}
        <div
          data-test-chart-tooltip
          class="fixed z-50 pointer-events-none px-2.5 py-1.5 rounded-lg text-xs shadow-lg border border-default bg-surface-elevated text-default -translate-x-1/2 -translate-y-full"
          style={{this.tooltipStyle}}
        >
          <div class="font-medium">{{this.tooltipData.category}}</div>
          <div class="text-gray-500 dark:text-gray-400">{{this.tooltipData.value}}</div>
        </div>
      {{/if}}
    </div>
  </template>
}

export type { LineChartSignature };
